import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useSeo } from '@/hooks/useSeo';
import { topics } from '@/mocks/topics';
import { hotList } from '@/mocks/hotList';
import PageHeader from '@/components/feature/PageHeader';
import TopicCard from '@/components/feature/TopicCard';
import ArticleCard from '@/components/feature/ArticleCard';

export default function Topics() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeSlug = searchParams.get('topic') ?? '';
  const activeTopic = topics.find((topic) => topic.slug === activeSlug) ?? null;

  useSeo({
    title: '瓜圈速报话题榜 - 娱乐热门榜单与话题分类聚合',
    description:
      '瓜圈速报话题榜按恋情观察、影视风向、综艺名场面等主题聚合娱乐热门榜单，把零散热点串成一条清晰的线索，方便系统追更。',
    keywords: '瓜圈速报话题榜,瓜圈速报热门榜单,娱乐话题分类,吃瓜话题聚合',
    path: '/topics',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: '瓜圈速报话题榜',
        description: '按主题聚合的娱乐热点话题榜，覆盖恋情、影视、综艺与舞台等方向。',
        hasPart: topics.map((topic) => ({
          '@type': 'Thing',
          name: topic.name,
          description: topic.description,
        })),
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://example.com/' },
          { '@type': 'ListItem', position: 2, name: '话题榜', item: 'https://example.com/topics' },
        ],
      },
    ],
  });

  const collection = useMemo(() => {
    const list = activeTopic ? hotList.filter((item) => item.category === activeTopic.name) : hotList;
    return [...list].sort((a, b) => b.heat - a.heat);
  }, [activeTopic]);

  const selectTopic = (slug: string) => {
    const next = new URLSearchParams(searchParams);
    if (slug) {
      next.set('topic', slug);
    } else {
      next.delete('topic');
    }
    setSearchParams(next);
  };

  return (
    <>
      <PageHeader
        crumbs={[{ label: '首页', to: '/' }, { label: '话题榜' }]}
        eyebrow="TOPIC BOARD"
        title="瓜圈速报话题榜"
        description="同一件事往往散落在好几个平台，话题榜把它们按主题归拢。选一个方向，就能顺着时间线一条条看下去。"
      />

      <section className="mx-auto w-full max-w-[1280px] px-4 py-10 md:px-6 md:py-14">
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => selectTopic('')}
            className={`cursor-pointer whitespace-nowrap rounded-full border px-4 py-2 text-sm font-medium transition ${
              !activeTopic
                ? 'border-primary-500 bg-primary-500 text-background-50'
                : 'border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-600'
            }`}
          >
            全部话题
          </button>
          {topics.map((topic) => (
            <button
              key={topic.id}
              type="button"
              onClick={() => selectTopic(topic.slug)}
              className={`inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border px-4 py-2 text-sm font-medium transition ${
                activeTopic?.slug === topic.slug
                  ? 'border-primary-500 bg-primary-500 text-background-50'
                  : 'border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-600'
              }`}
            >
              <i className={topic.icon} />
              {topic.name}
            </button>
          ))}
        </div>

        {activeTopic ? (
          <div className="mb-10 flex flex-col gap-3 rounded-card border border-background-200 bg-background-100 p-6 md:flex-row md:items-center md:justify-between md:p-7">
            <div>
              <div className="flex items-center gap-2.5">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100 text-xl text-primary-700">
                  <i className={activeTopic.icon} />
                </span>
                <h2 className="font-heading text-xl tracking-wide text-foreground-950 md:text-2xl">
                  {activeTopic.name}
                </h2>
              </div>
              <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600">
                {activeTopic.description}
              </p>
            </div>
            <div className="flex shrink-0 gap-8">
              <div>
                <p className="text-xs text-foreground-500">收录速报</p>
                <p className="mt-1 font-heading text-2xl tabular-nums text-foreground-950">
                  {activeTopic.articleCount}
                </p>
              </div>
              <div>
                <p className="text-xs text-foreground-500">话题热度</p>
                <p className="mt-1 font-heading text-2xl tabular-nums text-primary-600">{activeTopic.heat}</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="mb-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 md:gap-5">
            {topics.map((topic) => (
              <TopicCard key={topic.id} topic={topic} />
            ))}
          </div>
        )}

        <div className="mb-6 flex items-center justify-between">
          <h2 className="font-heading text-xl tracking-wide text-foreground-950 md:text-2xl">
            {activeTopic ? `${activeTopic.name} · 相关速报` : '全部速报'}
          </h2>
          <span className="text-sm text-foreground-500">共 {collection.length} 篇</span>
        </div>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {collection.map((item) => (
            <ArticleCard key={item.id} item={item} />
          ))}
        </div>

        <div className="mt-12 flex flex-col items-center gap-4 rounded-card border border-background-200 bg-background-100 px-6 py-10 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-100 text-2xl text-primary-600">
            <i className="ri-fire-line" />
          </span>
          <h2 className="font-heading text-xl tracking-wide text-foreground-950 md:text-2xl">
            想看纯粹的声量排序？
          </h2>
          <p className="max-w-lg text-sm leading-relaxed text-foreground-600">
            话题榜看的是趋势走向，热度排行看的是当下瞬间。两个榜单一起用，才不会错过突然冒头的瓜。
          </p>
          <Link
            to="/ranking"
            className="mt-1 inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition hover:bg-primary-600"
          >
            查看热度排行
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </section>
    </>
  );
}